/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode} from 'react';
import Portal from '@material-ui/core/Portal';
import Snackbar from '@material-ui/core/Snackbar';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import isString from 'lodash.isstring';
import {TertiaryButton} from '../components/buttons/TertiaryButton';
import {useMedia} from '../hooks/useMedia';
import {NotificationGeneralAlertIcon} from '../icons/NotificationGeneralAlertIcon';
import Render from '../render/Render';
import {toggleThemeStyle} from '../theme/helpers';
import {dlsColor, dlsDarkColor, dlsZIndex} from '../theme/theme';
import useThemeContext, {isLegacyTheme} from '../theme/useThemeContext';

interface StyleProps {
  maxWidth?: string;
  zIndex?: number;
  isDark?: boolean;
};

// defines the prop definitions for the session timeout ...
export type UserNotificationProps = {
  show: boolean;
  message: ReactNode;
  type?: string;
  actionLabel?: string;
  isIcon?: boolean;
  customAction?: boolean;
  customActionLabel?: string;
  customDismissLabel?: string;
  onDismiss?: () => void;
  onAction?: () => void;
  onClose?: () => void;
  autoHideDuration?: number;
  disableWindowBlurListener?: boolean;
  disableClickAwayListener?: boolean;
  partialSuccessState?: string | null;
} & StyleProps;

// returns the text color on an accent background, white in light mode ...
const getOnAccentColor = (isDark: boolean): string => (
  dlsDarkColor(isDark, 'textOnAccent', dlsColor('white'))
);

// defines the styles used by this component ...
const useStyles = makeStyles(theme => ({
  // legacy and current are separate rules, applied by theme - the legacy
  // rule is plain again (given through toggleThemeStyle, a whole rule loses
  // its nested selectors and props-driven values, which left the legacy
  // message unsized and clipped); the current rules keep their own form ...
  snackbarRoot : {
    display                        : 'flex',
    justifyContent                 : 'center',
    alignItems                     : 'center',
    width                          : '100%',
    zIndex                         : ({zIndex}: StyleProps) => zIndex ?? dlsZIndex('ionSnackbarRoot'),
    [theme.breakpoints.down('sm')] : {
      width : '97%'
    },
    '& .MuiSnackbarContent-message' : {
      height       : 'fit-content !important',
      maxWidth     : ({maxWidth}: StyleProps) => maxWidth,
      whiteSpace   : 'normal',
      overflowWrap : 'break-word'
    }
  },
  snackbarRootCurrent : toggleThemeStyle({}, {
    height    : '48px !important',
    zIndex    : ({zIndex}: StyleProps) => zIndex ?? dlsZIndex('ionSnackbarRoot'),
    display   : 'flex',
    boxShadow : `0px 3px 5px 0px rgba(0, 0, 0, 0.2),
    0px 1px 18px 0px rgba(0, 0, 0, 0.12),
    0px 6px 10px 0px rgba(0, 0, 0, 0.14)
    `?.replace(/\s+/g, ' ')?.trim(),
    alignItems                     : 'center',
    borderRadius                   : '12px !important',
    justifyContent                 : 'center',
    backgroundColor                : `${dlsColor('grey1')} !important`,
    [theme.breakpoints.down('sm')] : {
      width : '97%'
    },
    '& .MuiSnackbarContent-message' : {
      height                       : 'fit-content !important',
      [theme.breakpoints.up('lg')] : {
        maxWidth : ({maxWidth}: StyleProps) => maxWidth
      }
    }
  }),
  icon : {
    display       : 'inline-block',
    verticalAlign : 'middle',
    paddingTop    : 4,
    marginRight   : 24
  },
  snackbarMessageWrapper : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  // the rule is a function of the props so the live isDark is read, and the
  // legacy check is resolved inside it ...
  snackbarMessageWrapperCurrent : ({isDark = false}: StyleProps) => (isLegacyTheme() ? {} : {
    color          : `${getOnAccentColor(isDark)} !important`,
    display        : 'flex',
    fontSize       : '14px !important',
    alignItems     : 'center',
    fontFamily     : 'Inter !important',
    fontWeight     : 400,
    lineHeight     : '16px !important',
    letterSpacing  : '0px !important',
    justifyContent : 'center'
  }),
  action : {
    paddingLeft        : '0',
    '& [class*=label]' : {
      fontSize : '14px'
    }
  },
  snackbarPartialMessageWrapper : {
    fontSize : '12px'
  },
  snackbarAction : {
    color         : ({isDark = false}: StyleProps) => getOnAccentColor(isDark),
    cursor        : 'pointer',
    fontSize      : '13px',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    lineHeight    : '14px',
    marginRight   : '12px',
    letterSpacing : '0.5%'
  }
}) as any);

/**
 * The IonSnackbar component provides the default notification component within
 * the DLS which is to be used to display messages of interest to users at the bottom
 * of the page. The IonSnackbar is typically used for editors to inform users of an
 * error when performing a crud operation, such as editing an entity, etc..
 *
 * @see https://www.figma.com/file/z9yUzIaskZRhgpYIhLyh91/03_Hero-Screens?node-id=632%3A82003
 *
 * ```typescript
 *   import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
 *
 *   export const SessionTimeout = ({show, onLogin}: SessionTimeoutProps) => {
 *     return (
 *       <IonSnackbar show={show}
 *         icon={<NotificationDisconnectIcon />}
 *         title={translate('notifications:session.title')}
 *         message={translate('notifications:session.message')}
 *         actionLabel={translate('notifications:session.login')}
 *         onAction={onLogin}
 *         onClose={onClose} />
 *     );
 *   };
 *
 * ```
 *
 */
export const IonSnackbar = ({
  show,
  message,
  type,
  actionLabel,
  customDismissLabel,
  onDismiss,
  onAction,
  onClose,
  maxWidth,
  zIndex,
  isIcon = false,
  autoHideDuration,
  disableWindowBlurListener,
  disableClickAwayListener = false,
  partialSuccessState = null
}: UserNotificationProps) => {
  const {isCurrent: isCurrentTheme, isDark} = useThemeContext();
  const classes                             = useStyles({maxWidth, zIndex, isDark});
  const showIcon                            = partialSuccessState?.indexOf('/') > -1 || type === 'error';
  const {isPhone}                           = useMedia();
  const applyPartialStateStyles             = isPhone && partialSuccessState;
  const snackbarZIndex                      = zIndex ?? dlsZIndex('ionSnackbarRoot');

  if (show) {
    return (
      <Portal>
        <Snackbar open
          anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
          className={isCurrentTheme ? classes.snackbarRootCurrent : classes.snackbarRoot}
          style={{zIndex: snackbarZIndex}}
          action={
            (customDismissLabel || actionLabel) &&
            <>
              {customDismissLabel &&
                <Render>
                  <Render.If condition={isCurrentTheme}>
                    <span className={classes.snackbarAction} onClick={onDismiss}>{customDismissLabel}</span>
                  </Render.If>
                  <Render.Else>
                    <TertiaryButton onClick={onDismiss} isAlt>{customDismissLabel}</TertiaryButton>
                  </Render.Else>
                </Render>
              }
              {actionLabel &&
                <Render>
                  <Render.If condition={isCurrentTheme}>
                    <span className={classes.snackbarAction} onClick={onAction}>{actionLabel}</span>
                  </Render.If>
                  <Render.Else>
                    <TertiaryButton usecaactive onClick={onAction} isAlt>{actionLabel}</TertiaryButton>
                  </Render.Else>
                </Render>
              }
            </>
          }
          message={
            <div className={classnames(
              isCurrentTheme ? classes.snackbarMessageWrapperCurrent : classes.snackbarMessageWrapper,
              applyPartialStateStyles ? classes.snackbarPartialMessageWrapper : undefined
            )}>
              <div style={{display: isIcon || showIcon ? 'initial' : 'none'}} className={classes.icon}>
                <NotificationGeneralAlertIcon color={getOnAccentColor(isDark)} />
              </div>
              {partialSuccessState && <span>{partialSuccessState}&nbsp;</span>}
              {!isString(message) ? <span>{message}</span> : message}
            </div>
          }
          ClickAwayListenerProps={disableClickAwayListener ? {
            onClickAway : function(e) {
              e.preventDefault();
              e.stopPropagation();
            }
          } : null}
          disableWindowBlurListener={disableWindowBlurListener}
          autoHideDuration={autoHideDuration || null}
          onClose={onClose}
          ContentProps={{
            classes : {
              action : applyPartialStateStyles ? classes.action : undefined
            }
          }} />
      </Portal>
    );
  }
  return null;
};

export default IonSnackbar;
